import i18n from "../i18n";
// owner.service.ts
import { AxiosResponse } from "axios";
import api from "./api"; // Axios instance
import { ICarWashObject } from "../types/carWash";
import {
  IUserObject,
  ICreateOwnerRes,
  IGetOwnersRes,
  IGetOwnerRes,
  IGetOwnerCarWashesRes,
  IOwnerObject,
  OwnerResponse,
  IMyOwnersRes,
  IEmployeeRequest,
  IEmployeeUpdateRequest,
  IOwnerStatistics,
  IOwnerStatisticsSeries,
  OwnerStatisticsRange,
  IOwnerContract,
  IOwnerContractUploadRes,
} from "./api.types";

class OwnerService {
  // Create new owner
  public async createOwner(ownerData: Partial<IOwnerObject>): Promise<ICreateOwnerRes> {
    try {
      const response: AxiosResponse<ICreateOwnerRes> =
        await api.post<ICreateOwnerRes>("/admins/owners", ownerData);
      return response.data;
    } catch (error: any) {
      this.handleError(error, i18n.t("serviceErrors.createOwner"));
    }
  }

  // Get list of owners (with optional filtering, e.g., active owners)
  public async getOwners(): Promise<IGetOwnersRes> {
    try {
      const response: AxiosResponse<IGetOwnersRes> =
        await api.get<IGetOwnersRes>("/admins/owners");
      return response.data;
    } catch (error: any) {
      this.handleError(error, i18n.t("serviceErrors.fetchOwners"));
    }
  }

  public async getOwnerById(ownerID: string): Promise<IOwnerObject> {
    try {
      const response: AxiosResponse<IGetOwnerRes> = await api.get<IGetOwnerRes>(
        `/admins/owners/${ownerID}`
      );
      return response.data.data;
    } catch (error: any) {
      this.handleError(error, i18n.t("serviceErrors.fetchOwner"));
    }
  }

  // Get car washes for a specific owner
  public async getOwnerCarWashes(ownerID: string): Promise<ICarWashObject[]> {
    try {
      const response: AxiosResponse<IGetOwnerCarWashesRes> =
        await api.get<IGetOwnerCarWashesRes>(
          `/admins/owners/${ownerID}/carWashes`
        );
      return response.data.data;
    } catch (error: any) {
      this.handleError(error, i18n.t("serviceErrors.fetchOwnerCarWashes"));
    }
  }

  // Update owner details by owner ID
  public async updateOwner(
    ownerId: string,
    ownerData: Partial<IUserObject>
  ): Promise<OwnerResponse> {
    try {
      const response = await api.put<OwnerResponse>(
        `/admins/owners/${ownerId}`,
        ownerData
      );
      return response.data;
    } catch (error: any) {
      this.handleError(error, i18n.t("serviceErrors.updateOwner"));
    }
  }

  // Delete an owner by owner ID
  public async deleteOwner(ownerId: string): Promise<{ status: string }> {
    try {
      const response = await api.delete<{ status: string }>(
        `/admins/owners/${ownerId}`
      );
      return response.data;
    } catch (error: any) {
      this.handleError(error, i18n.t("serviceErrors.deleteOwner"));
    }
  }

  // ---------------------------------------------------------------------------
  // Phase 26 — RM-scoped pending-owner rotation methods.
  // These endpoints use the ApiResponse.success envelope `{ status, message, data }`
  // (unlike the legacy getOwners which returns a raw `{ status, results, data }`),
  // so getMyOwners / getMyPendingOwners read `response.data.data` (RESEARCH Pitfall 5).
  // ---------------------------------------------------------------------------

  // RM "All" tab — owners scoped to the current RM, excluding pending approvals.
  public async getMyOwners(): Promise<IOwnerObject[]> {
    try {
      const response: AxiosResponse<IMyOwnersRes> = await api.get<IMyOwnersRes>(
        "/admins/owners/my-owners"
      );
      return response.data.data;
    } catch (error: any) {
      this.handleError(error, i18n.t("serviceErrors.fetchMyOwners"));
    }
  }

  // RM "Pending" tab — pending-approval owners scoped to the current RM.
  public async getMyPendingOwners(): Promise<IOwnerObject[]> {
    try {
      const response: AxiosResponse<IMyOwnersRes> = await api.get<IMyOwnersRes>(
        "/admins/owners/my-pending"
      );
      return response.data.data;
    } catch (error: any) {
      this.handleError(error, i18n.t("serviceErrors.fetchMyPendingOwners"));
    }
  }

  // Start — claims the pending owner and begins the 48h SLA.
  public async startOwner(id: string): Promise<OwnerResponse> {
    try {
      const response = await api.post<OwnerResponse>(
        `/admins/owners/${id}/start`
      );
      return response.data;
    } catch (error: any) {
      this.handleError(error, i18n.t("serviceErrors.startOwner"));
    }
  }

  // Accept — approves the pending owner (moves to the All tab).
  public async acceptOwner(id: string): Promise<OwnerResponse> {
    try {
      const response = await api.post<OwnerResponse>(
        `/admins/owners/${id}/accept`
      );
      return response.data;
    } catch (error: any) {
      this.handleError(error, i18n.t("serviceErrors.acceptOwner"));
    }
  }

  // Reject — returns the pending owner to the rotation pool.
  public async rejectOwner(id: string): Promise<OwnerResponse> {
    try {
      const response = await api.post<OwnerResponse>(
        `/admins/owners/${id}/reject`
      );
      return response.data;
    } catch (error: any) {
      this.handleError(error, i18n.t("serviceErrors.rejectOwner"));
    }
  }

  // ---------------------------------------------------------------------------
  // Phase 27 — RM-scoped employee CRUD on the owner detail page.
  // New endpoints use the ApiResponse.success envelope `{ status, message, data }`,
  // so the payload is read from response.data.data (RESEARCH Pitfall 3).
  // ---------------------------------------------------------------------------

  // Create a new employee under the given owner.
  public async createEmployee(ownerID: string, body: IEmployeeRequest) {
    try {
      const response = await api.post(
        `/admins/owners/${ownerID}/employees`,
        body
      );
      return response.data.data;
    } catch (error: any) {
      this.handleError(error, i18n.t("serviceErrors.createEmployee"));
    }
  }

  // Update an existing employee (firstName, lastName, userType, active).
  public async updateEmployee(
    ownerID: string,
    employeeId: string,
    body: IEmployeeUpdateRequest
  ) {
    try {
      const response = await api.patch(
        `/admins/owners/${ownerID}/employees/${employeeId}`,
        body
      );
      return response.data.data;
    } catch (error: any) {
      this.handleError(error, i18n.t("serviceErrors.updateEmployee"));
    }
  }

  // Hard-delete an employee and remove it from the owner's employees array.
  public async deleteEmployee(ownerID: string, employeeId: string) {
    try {
      const response = await api.delete(
        `/admins/owners/${ownerID}/employees/${employeeId}`
      );
      return response.data.data;
    } catch (error: any) {
      this.handleError(error, i18n.t("serviceErrors.deleteEmployee"));
    }
  }

  // Owner wash statistics — total / completed / cancelled wash counts.
  public async getOwnerStatistics(
    ownerID: string
  ): Promise<IOwnerStatistics> {
    try {
      const response = await api.get(`/admins/owners/${ownerID}/statistics`);
      return response.data.data;
    } catch (error: any) {
      this.handleError(error, i18n.t("serviceErrors.fetchStatistics"));
    }
  }

  // Owner wash statistics time series — one request returns all metrics'
  // series for that range.
  public async getOwnerStatisticsSeries(
    ownerID: string,
    range: OwnerStatisticsRange
  ): Promise<IOwnerStatisticsSeries> {
    try {
      const response = await api.get(
        `/admins/owners/${ownerID}/statistics/series`,
        { params: { range } }
      );
      return response.data.data;
    } catch (error: any) {
      this.handleError(error, i18n.t("serviceErrors.fetchStatisticsSeries"));
    }
  }

  // ---------------------------------------------------------------------------
  // Phase 38 — owner contract PDF (D-01/D-02/D-05).
  // Both endpoints use the ApiResponse.success envelope `{ status, message, data }`,
  // so the payload is read from response.data.data.
  // ---------------------------------------------------------------------------

  // Read contract metadata + a freshly-minted, short-lived presigned download URL.
  // Available to super_admin and (RM-scoped) relationship_manager; the backend
  // returns 404 for an owner an RM does not manage.
  public async getOwnerContract(ownerID: string): Promise<IOwnerContract> {
    try {
      const response = await api.get(`/admins/owners/${ownerID}/contract`);
      return response.data.data;
    } catch (error: any) {
      this.handleError(error, i18n.t("serviceErrors.fetchContract"));
    }
  }

  // Upload (or replace — D-01, deterministic S3 key) the owner's contract PDF.
  // The field name MUST be "contract" to match the backend's upload.single("contract");
  // any other name produces an opaque multer LIMIT_UNEXPECTED_FILE error.
  // super_admin only — enforced server-side by restrictTo("super_admin").
  public async uploadOwnerContract(
    ownerID: string,
    file: File
  ): Promise<IOwnerContractUploadRes> {
    try {
      const formData = new FormData();
      formData.append("contract", file);
      const response = await api.post(
        `/admins/owners/${ownerID}/contract`,
        formData,
        { headers: { "Content-Type": "multipart/form-data" } }
      );
      return response.data.data;
    } catch (error: any) {
      this.handleError(error, i18n.t("serviceErrors.uploadContract"));
    }
  }

  // Error handling method
  private handleError(error: any, defaultMessage: string): never {
    if (error.response && error.response.data) {
      throw new Error(error.response.data.message || defaultMessage);
    } else {
      throw new Error(i18n.t("serviceErrors.serverError"));
    }
  }
}

const ownerServiceInstance = new OwnerService();
export default ownerServiceInstance;
